import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Clock, Ban } from 'lucide-react';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';

type ErrorCode =
  | 'invalid'
  | 'expired'
  | 'already-confirmed'
  | 'already-used'
  | 'cancelled'
  | 'unknown';

type PageState =
  | { status: 'loading' }
  | { status: 'success' }
  | { status: 'error'; code: ErrorCode };

class TokenError extends Error {
  code: ErrorCode;
  constructor(code: ErrorCode) {
    super(code);
    this.code = code;
  }
}

async function cancelOrderByToken(token: string): Promise<void> {
  const res = await fetch('/api/orders/cancel-by-token', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (res.ok) return;

  const body: { error?: string; code?: string } = await res
    .json()
    .catch(() => ({}));

  if (res.status === 404) throw new TokenError('invalid');
  if (res.status === 410) throw new TokenError('expired');
  if (res.status === 409 && body.code === 'cancelled')
    throw new TokenError('cancelled');
  if (res.status === 409 && body.code === 'already-used')
    throw new TokenError('already-used');
  if (res.status === 409) throw new TokenError('already-confirmed');
  throw new TokenError('unknown');
}

const ERROR_CONFIG: Record<
  ErrorCode,
  {
    icon: React.ReactNode;
    titleKey: string;
    messageKey: string;
    ctaKey?: string;
    ctaHref?: string;
  }
> = {
  invalid: {
    icon: <XCircle className="text-red-500" size={48} />,
    titleKey: 'cancel_order.error.invalid.title',
    messageKey: 'cancel_order.error.invalid.message',
  },
  expired: {
    icon: <Clock className="text-amber-500" size={48} />,
    titleKey: 'cancel_order.error.expired.title',
    messageKey: 'cancel_order.error.expired.message',
    ctaKey: 'cancel_order.error.expired.cta',
    ctaHref: '/',
  },
  'already-confirmed': {
    icon: <CheckCircle className="text-blue-500" size={48} />,
    titleKey: 'cancel_order.error.already_confirmed.title',
    messageKey: 'cancel_order.error.already_confirmed.message',
  },
  'already-used': {
    icon: <CheckCircle className="text-blue-500" size={48} />,
    titleKey: 'cancel_order.error.already_used.title',
    messageKey: 'cancel_order.error.already_used.message',
  },
  cancelled: {
    icon: <Ban className="text-gray-400" size={48} />,
    titleKey: 'cancel_order.error.cancelled.title',
    messageKey: 'cancel_order.error.cancelled.message',
  },
  unknown: {
    icon: <XCircle className="text-red-500" size={48} />,
    titleKey: 'cancel_order.error.unknown.title',
    messageKey: 'cancel_order.error.unknown.message',
    ctaKey: 'cancel_order.error.unknown.cta',
    ctaHref: '/',
  },
};

export function CancelOrder() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('cancel-order');
  const token = searchParams.get('token');
  const [state, setState] = useState<PageState>(
    token ? { status: 'loading' } : { status: 'error', code: 'invalid' }
  );
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    // StrictMode double-invokes effects in dev; the second mount must not re-POST
    // because the backend marks the token used on the first call (HTTP 409 on retry).
    if (requestedRef.current) return;
    requestedRef.current = true;

    cancelOrderByToken(token)
      .then(() => setState({ status: 'success' }))
      .catch((err: unknown) => {
        const code: ErrorCode =
          err instanceof TokenError ? err.code : 'unknown';
        setState({ status: 'error', code });
      });
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <PageLoadingSpinner name={t('cancel_order.loading')} />
      </div>
    );
  }

  if (state.status === 'error') {
    return <ErrorView code={state.code} />;
  }

  return <SuccessView />;
}

function SuccessView() {
  const { t } = useTranslation('cancel-order');

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 p-4"
      data-testid="cancel-order-success"
    >
      <div className="bg-white rounded-xl shadow-md border border-gray-200 max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <Ban className="text-gray-400" size={48} />
          <h1 className="text-xl font-bold text-gray-900">
            {t('cancel_order.success.title')}
          </h1>
          <p className="text-sm text-gray-600">
            {t('cancel_order.success.message')}
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorView({ code }: { code: ErrorCode }) {
  const { t } = useTranslation('cancel-order');
  const config = ERROR_CONFIG[code];

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 p-4"
      data-testid="cancel-order-error"
    >
      <div className="bg-white rounded-xl shadow-md border border-gray-200 max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          {config.icon}
          <h1 className="text-xl font-bold text-gray-900">
            {t(config.titleKey)}
          </h1>
          <p className="text-sm text-gray-600">{t(config.messageKey)}</p>
          {config.ctaKey && config.ctaHref && (
            <Link
              to={config.ctaHref}
              className="mt-2 text-sm font-medium text-primary hover:underline"
              data-testid="error-cta-link"
            >
              {t(config.ctaKey)}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
